/**
 * Date helper utilities for handling display dates with fallback logic
 */

/**
 * Get the display date from an item with fallback logic
 * Priority: displayDate -> updatedAt -> createdAt
 * @param {Object} item - The item object (publication, advert, etc.)
 * @returns {string} - The date to display
 */
export const getDisplayDate = (item) => {
  // Use displayDate if available, otherwise fall back to updatedAt, then createdAt
  return item.displayDate || item.updatedAt || item.createdAt;
};

/**
 * Format the display date for UI display
 * @param {Object} item - The item object (publication, advert, etc.)
 * @param {string} language - The language for formatting ('en', 'fr', 'rw')
 * @returns {string} - Formatted date string
 */
export const formatDisplayDate = (item, language = 'en') => {
  const date = getDisplayDate(item);
  if (!date) return '';
  
  try {
    // Map language codes to proper locale identifiers
    const localeMap = {
      'en': 'en-US',
      'fr': 'fr-FR',
      'rw': 'en-US' // Fallback to English for Kinyarwanda since it's not widely supported
    };
    
    const locale = localeMap[language] || 'en-US';
    
    return new Date(date).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (error) {
    // Fallback to simple date format if locale formatting fails
    console.warn('Date formatting failed:', error);
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
};

/**
 * Format the display date for datetime-local input
 * @param {Object} item - The item object (publication, advert, etc.)
 * @returns {string} - Formatted date string for datetime-local input (YYYY-MM-DDTHH:MM)
 */
export const formatDateForInput = (item) => {
  const date = getDisplayDate(item);
  if (!date) return '';
  
  try {
    return new Date(date).toISOString().slice(0, 16);
  } catch (error) {
    return '';
  }
};

/**
 * Get a human-readable relative time string
 * @param {Object} item - The item object (publication, advert, etc.)
 * @returns {string} - Relative time string (e.g., "2 days ago")
 */
export const getRelativeTime = (item) => {
  const date = getDisplayDate(item);
  if (!date) return '';
  
  try {
    const now = new Date();
    const itemDate = new Date(date);
    const diffInMs = now - itemDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
    return `${Math.floor(diffInDays / 365)} years ago`;
  } catch (error) {
    return '';
  }
};
